import i18next from 'i18next'
import { z } from 'zod'

import { abTestTrafficSplitTypes } from 'features/ab-tests/types/abTestTrafficSplit'
import { enabled } from 'features/globalSetup/forms/Prebid/Modules/schemas/editSchema'
import { auctionDelay, prebidTimeout } from 'features/globalSetup/pages/Defaults/PrebidStack'
import {
  csEnabled,
  priority,
  s2sEnabled,
} from 'features/globalSetup/pages/Prebid/Bidders/EditBidderPage/pages/BidParamsAndConnection/form/schema'
import {
  codeAfter,
  codeBefore,
  customCodeEnabled,
} from 'features/inventory/domain/forms/GeneralForm/schemas/editSchema'
import {
  customAuctionDelayEnabled,
  customPrebidTimeoutEnabled,
  domainAmazonEnabled,
  prebidStackEnabled,
} from 'features/inventory/domain/forms/PrebidStackForm/schema'
import {
  desktopAdThreshold,
  desktopMonitorUserScrollEnabled,
  desktopRefreshInterval,
  mobileAdThreshold,
  mobileMonitorUserScrollEnabled,
  mobileRefreshInterval,
} from 'features/inventory/domain/forms/RefresherForm/schema'
import { unfilledRecoveryEnabled } from 'features/inventory/domain/forms/UnfilledRecoveryForm/schema'
import {
  gamAdRenderMargin,
  gamAdRequestMargin,
  kvTargetingAndPrebidAuction,
  lazyLoadTargetingPrebidAuctionEnabled,
  lazyLoadType,
  mobileScaling,
  validateLazyLoadingSchema,
} from 'features/inventory/domain/forms/ViewabilityToolsForm/lazyLoadingSchema'
import { unfilledRecoveryPrebidBehaviours } from 'features/inventory/types/unfilledRecoveryPrebidBehaviour'
import { REPORTED_KEY_VALUE_DISALLOWED_CHARACTERS } from './constants'

import type { AbTestParametersSchema } from 'features/ab-tests/types/abTestParametersSchema'

const parameters: z.ZodType<AbTestParametersSchema> = z.custom<AbTestParametersSchema>()
export type Parameters = z.infer<typeof parameters>

const bidders = z
  .record(
    z.string(),
    z.object({
      csEnabled,
      priority,
      s2sEnabled,
    }),
  )
  .optional()
export type BiddersInput = z.infer<typeof bidders>

const prebidModule = z
  .record(
    z.string(),
    z.object({
      enabled,
    }),
  )
  .optional()
export type PrebidModuleInput = z.infer<typeof prebidModule>

const abTestVariantsSchema = z
  .object({
    general: z
      .object({
        customCode: z
          .object({
            codeAfter: codeAfter.optional(),
            codeBefore: codeBefore.optional(),
            customCodeEnabled: customCodeEnabled.optional(),
          })
          .optional(),
      })
      .optional(),
    prebidStack: z
      .object({
        auctionDelay: z
          .object({
            auctionDelay: auctionDelay.optional(),
            customAuctionDelayEnabled: customAuctionDelayEnabled.optional(),
          })
          .optional(),
        bidders,
        domainAmazonEnabled: domainAmazonEnabled.optional(),
        prebidModules: z
          .object({
            analytics: prebidModule,
            general: prebidModule,
            userId: prebidModule,
            vendorSpecific: prebidModule,
          })
          .optional(),
        prebidStackEnabled: prebidStackEnabled.optional(),
        prebidTimeout: z
          .object({
            customPrebidTimeoutEnabled: customPrebidTimeoutEnabled.optional(),
            prebidTimeout: prebidTimeout.optional(),
          })
          .optional(),
      })
      .optional(),
    refresher: z
      .object({
        basicSettings: z.object({
          desktopAdThreshold,
          desktopMonitorUserScrollEnabled,
          desktopRefreshInterval,
          mobileAdThreshold,
          mobileMonitorUserScrollEnabled,
          mobileRefreshInterval,
        }),
      })
      .optional(),
    reportedKeyValue: z
      .string()
      .min(1)
      .refine(
        (value: string): boolean => value.match(REPORTED_KEY_VALUE_DISALLOWED_CHARACTERS) === null,
        i18next.t(
          'features/abTests:common.variants.table.reportedKeyValue.disallowedCharactersError',
        ),
      ),
    trafficShare: z.number().min(0).max(100).multipleOf(0.1).optional(),
    unfilledRecovery: z
      .object({
        prebidBehaviourType: z.enum(unfilledRecoveryPrebidBehaviours).optional(),
        unfilledRecoveryEnabled,
      })
      .optional(),
    viewabilityTools: z
      .object({
        lazyLoading: z.object({
          gamAdRenderMargin,
          gamAdRequestMargin,
          kvTargetingAndPrebidAuction,
          lazyLoadTargetingPrebidAuctionEnabled,
          lazyLoadType,
          mobileScaling,
        }),
      })
      .optional(),
  })
  .superRefine(({ viewabilityTools }: Partial<VariantsSchema>, ctx: z.RefinementCtx): void => {
    if (viewabilityTools?.lazyLoading !== undefined) {
      return validateLazyLoadingSchema(
        viewabilityTools.lazyLoading,
        ctx,
        'viewabilityTools.lazyLoading',
      )
    }
  })

export type VariantsSchema = z.infer<typeof abTestVariantsSchema>

const variantKeys = z.union([
  z.literal('variantA'),
  z.literal('variantB'),
  z.literal('variantC'),
  z.literal('variantD'),
  z.literal('variantE'),
  z.literal('variantF'),
  z.literal('variantG'),
  z.literal('variantH'),
  z.literal('variantI'),
  z.literal('variantJ'),
])
export type VariantKey = z.infer<typeof variantKeys>

export const variants = z.record(variantKeys, abTestVariantsSchema.optional())
export type Variants = z.infer<typeof variants>

export const abTestsCommonSchema = z.object({
  cookieLifetime: z.number().min(1).max(30).optional(),
  domainId: z.string().nonempty(i18next.t('common:form.error.nonEmpty')),
  name: z.string(),
  parameters: parameters.optional(),
  trafficSplitType: z.enum(abTestTrafficSplitTypes).optional(),
  variants: variants.optional(),
})

export type AbTestsCommonSchema = z.infer<typeof abTestsCommonSchema>

export const abTestsCommonSchemaDefaults: AbTestsCommonSchema = {
  cookieLifetime: 7,
  domainId: '',
  name: '',
  trafficSplitType: 'sessionStorage',
}
